import {
  resolveApiBase,
  SportsApiClient,
  type WidgetProps,
} from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'

interface StandingRow {
  position: number | null
  team: string
  logo: string | null
  played: number | null
  won: number | null
  drawn: number | null
  lost: number | null
  goalDiff: number | null
  points: number | null
}
interface StandingsResponse {
  leagueName: string
  rows: StandingRow[]
}

export const LEAGUE_TABLE_ATTRIBUTES = [
  'sport',
  'league',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface LeagueTableProps extends WidgetProps {
  sport?: string
  league?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

export const leagueTableStyles = `
  .table-wrap { overflow-x: auto; }
  table.sports-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
  table.sports-table th, table.sports-table td { padding: 0.5rem 0.4rem; }
  table.sports-table thead th {
    font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--sports-muted); font-weight: 600; text-align: right;
    border-bottom: 1px solid var(--sports-border);
  }
  table.sports-table thead th.teamh { text-align: left; padding-left: 0.9rem; }
  table.sports-table td { border-bottom: 1px solid var(--sports-border); text-align: right; font-variant-numeric: tabular-nums; }
  table.sports-table tbody tr:hover { background: color-mix(in srgb, var(--sports-fg) 4%, transparent); }
  td.rank { width: 1.9rem; text-align: center; color: var(--sports-muted); font-variant-numeric: tabular-nums; }
  td.pts { font-weight: 700; }
  td.team { text-align: left; max-width: 15rem; }
  .teamcell { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
  .teamcell img { width: 18px; height: 18px; object-fit: contain; flex: none; }
  .teamcell .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .teamcell .grp { color: var(--sports-muted); font-size: 0.72rem; }
  .see-more {
    display: block; width: 100%; padding: 0.6rem 1rem; text-align: center;
    background: var(--sports-card); color: var(--sports-muted);
    border: none; border-top: 1px solid var(--sports-border);
    font: inherit; font-size: 0.82rem; cursor: pointer;
  }
  .see-more:hover { color: var(--sports-fg); }
`

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const dash = (v: number | null): string => (v == null ? '—' : String(v))

export function LeagueTable({
  sport = 'basketball',
  league,
  lang = 'es',
  limit,
  heading,
  refresh,
  apiBase,
}: LeagueTableProps) {
  const max = number(limit, 30)
  // Tables only move when a game finishes, so the default is far slower than
  // the livescores widget's; `refresh="0"` turns polling off entirely.
  const refreshSeconds = number(refresh, 600)
  const base = resolveApiBase(apiBase)
  const t = useLabels(base, lang)
  const [expanded, setExpanded] = useState(false)

  const { data, error, loading } = useFeed(
    (signal) =>
      league
        ? new SportsApiClient(base).leagueStandings<StandingsResponse>(sport, league, lang, signal)
        : Promise.resolve(null),
    [base, sport, league, lang],
    league ? refreshSeconds : 0,
  )

  if (!league) {
    return (
      <div class="sports-card">
        <div class="sports-note error">
          &lt;sports-league-table&gt; needs a <code>league</code> attribute.
        </div>
      </div>
    )
  }

  const rows = (data?.rows ?? []).slice(0, max)
  // Show the first 10 rows by default; the "Ver más" footer reveals the rest.
  const visible = expanded ? rows : rows.slice(0, 10)
  // Only render the columns the sport actually populates: basketball is W/L only
  // (no draws, no points); football has draws and points.
  const hasDraw = rows.some((r) => r.drawn != null)
  const hasPoints = rows.some((r) => r.points != null)
  const title = heading || data?.leagueName || ''

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{title}</span>
      </div>

      {loading && <div class="sports-note">{t('loading', 'Cargando…')}</div>}

      {!loading && error && (
        <div class="sports-note error">{t('tableUnavailable', 'La clasificación no está disponible.')}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div class="sports-note">{t('noStandings', 'Sin clasificación publicada.')}</div>
      )}

      {rows.length > 0 && (
        <div class="table-wrap">
          <table class="sports-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="teamh">{t('team', 'Equipo')}</th>
                <th>{t('played', 'PJ')}</th>
                <th>{t('won', 'G')}</th>
                {hasDraw && <th>{t('drawn', 'E')}</th>}
                <th>{t('lost', 'P')}</th>
                {hasPoints && <th>{t('points', 'Pts')}</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, index) => (
                <tr key={row.team}>
                  <td class="rank">{row.position ?? index + 1}</td>
                  <td class="team">
                    <span class="teamcell">
                      {row.logo && (
                        <img
                          src={row.logo}
                          alt=""
                          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                        />
                      )}
                      <span class="nm">{row.team}</span>
                    </span>
                  </td>
                  <td>{dash(row.played)}</td>
                  <td>{dash(row.won)}</td>
                  {hasDraw && <td>{dash(row.drawn)}</td>}
                  <td>{dash(row.lost)}</td>
                  {hasPoints && <td class="pts">{dash(row.points)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 10 && (
        <button class="see-more" type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('seeLess', 'Ver menos') : `${t('seeMore', 'Ver más')} (${rows.length})`}
        </button>
      )}
    </div>
  )
}
